import { createContext, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportService } from '../../../shared/services/reports/reportService';

export interface ReportFilters {
  type: 'Moradores' | 'Visitantes' | 'Reservas';
  period: string;
  startDate?: string;
  endDate?: string;
}

export interface ReportSummary {
  residentsTotal: number;
  residentsActive: number;
  residentsInactive: number;
  visitorsTotal: number;
  visitorsThisMonth: number;
  visitorsDailyAvg: number;
  reservationsTotal: number;
  reservationsConfirmed: number;
  reservationsCancelled: number;
}

export interface ReportDetail {
  type: string;
  name: string;
  date: string;
  status: string;
}

interface ReportContextData {
  filters: ReportFilters;
  setFilters: (filters: ReportFilters) => void;
  summary: ReportSummary | null;
  details: ReportDetail[];
  isLoading: boolean;
  refetch: () => void;
}

export const ReportContext = createContext<ReportContextData>({} as ReportContextData);

export function ReportProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<ReportFilters>({ type: 'Moradores', period: 'Último mês' });

  const { data, isLoading, refetch } = useQuery<{ summary: ReportSummary; details: ReportDetail[] }>({
    queryKey: ['reports', filters],
    queryFn: () => reportService.getReports(filters),
  });

  return (
    <ReportContext.Provider
      value={{
        filters,
        setFilters,
        summary: data?.summary ?? null,
        details: data?.details ?? [],
        isLoading,
        refetch,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}
